// libs
import React, { memo, useContext, useMemo } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// components
import Carousel from 'shared/components/Carousel';
import NavButton, { NAV_BUTTON_TYPES } from './NavButtons';
import WidgetList from './WidgetList';

// context
import ResponsiveContext from 'shared/components/ResponsiveLoader/ResponsiveContext';

// defs
import { IWidgetCarousel } from './types';

// helpers
import { getCarouselProps } from './helpers';

// constants
import { CAROUSEL_TYPES, DEFAULT_CHILD_WIDTH_RATIO, HEIGHT_BUFFERS } from './constants';
import { MARGIN_RIGHT } from 'shared/components/AdPlatformWidgets/Desktop/constant';
import { WIDGET_TYPES } from 'shared/components/AdPlatformWidgets/constants';

function WidgetCarousel({
  carouselType,
  products,
  widgets,
  containerWidth = 0,
  inventoryName,
}: IWidgetCarousel) {
  const { deviceWidth } = useContext(ResponsiveContext);
  const contentAlign = get(widgets, 'parameters.content_align', 'left');

  const widgetType = get(widgets, 'wtype', WIDGET_TYPES.SLIDING_WIDGET);

  let widgetWidth: number,
    slidesToShow: number,
    slideHeight: number,
    aspect_ratio,
    slideHeightBuffer = 0;

  if (carouselType === CAROUSEL_TYPES.WIDGETS) {
    let widthPercentage =
      get(widgets, 'parameters.child_width_desktop') || get(widgets, 'parameters.child_width');

    if (widthPercentage === undefined) {
      widgetWidth = (containerWidth * DEFAULT_CHILD_WIDTH_RATIO) / 100;
    } else {
      widgetWidth = (containerWidth * widthPercentage) / 100;
    }

    const hasTitle = !isEmpty(
      get(widgets, 'children.0.parameters.title') ||
        get(widgets, 'children.0.parameters.title_plain', '')
    );
    const hasDescription = !isEmpty(get(widgets, 'children.0.parameters.description', ''));

    if (!hasTitle && !hasDescription) {
      slideHeightBuffer = HEIGHT_BUFFERS.BASE;
    }
    if (hasTitle) {
      slideHeightBuffer = slideHeightBuffer + HEIGHT_BUFFERS.TITLE;
    }
    if (hasDescription) {
      slideHeightBuffer = slideHeightBuffer + HEIGHT_BUFFERS.DESCRIPTION;
    }

    aspect_ratio = get(widgets, 'children.0.children.0.source.Web_aspect_ratio', 1);
    slidesToShow = Math.floor(containerWidth / widgetWidth);
    slideHeight = widgetWidth / aspect_ratio + slideHeightBuffer;
  }

  const carouselProps =
    carouselType === CAROUSEL_TYPES.PRODUCTS
      ? getCarouselProps(deviceWidth, carouselType)
      : {
          slideWidth: widgetWidth,
          slideHeight: slideHeight,
          slidesToMove: 1,
          slidesToShow: slidesToShow,
        };

  const carouselBackground = get(widgets, 'parameters.bkg_color', '');

  const CarouselChildren =
    carouselType === CAROUSEL_TYPES.PRODUCTS
      ? WidgetList({
          widgetType: CAROUSEL_TYPES.PRODUCTS,
          widgets: products,
          contentAlign,
          widgetWidth: carouselProps.slideWidth,
        })
      : WidgetList({
          widgetType: CAROUSEL_TYPES.WIDGETS,
          widgets: widgets.children,
          widgetWidth,
          contentAlign,
          inventoryName,
        });

  const containerStyle = useMemo(
    () => ({
      width: `${containerWidth}px`,
      backgroundColor: `${carouselBackground}`,
      ...(widgetType == WIDGET_TYPES.IN_FOCUS ? { position: 'absolute', right: '0' } : {}),
    }),
    [carouselBackground, containerWidth, widgetType]
  );

  return (
    <div style={containerStyle}>
      <Carousel
        {...carouselProps}
        nextButton={<NavButton type={NAV_BUTTON_TYPES.NEXT} />}
        prevButton={<NavButton type={NAV_BUTTON_TYPES.PREV} />}
        ignoreRightMargin
        customRightMargin={MARGIN_RIGHT}
        navPadding={0}
        navCustomStyles={{ minWidth: '30px' }}
      >
        {CarouselChildren}
      </Carousel>
    </div>
  );
}

export default memo(WidgetCarousel);
